/**
 * Application entry point.
 *
 * Provider order is the dependency order: QueryClient, then the session
 * context that is populated from a query, then the router that renders using
 * it. Exactly one QueryClientProvider and exactly one session context exist in
 * the application (ADR-14).
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { App } from './App';
import { SessionScopeProvider } from './session/SessionScopeContext';
import { ExternalViewerApp, isExternalViewerPath } from './routes/external/ExternalViewerApp';

import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Atlas data is scoped per caller and changes on human timescales, so
      // refetching on every window focus is noise rather than freshness.
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,
      // Never retry a refused or expired session. Recovery is one trip to the
      // sign-in screen, handled in the API client; retrying would fire one
      // attempt per retry against the same dead session.
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 1;
      },
    },
  },
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root is missing from index.html.');
}

// Provider order is dependency order: the query client, the session read from
// it, then the router that renders using it. The session is the API's
// HttpOnly cookie (f17.3); nothing in the browser holds a credential.
//
// f15.5: an external recipient's link is not part of the application at all.
// It gets its own root with no session query.
createRoot(container).render(
  <StrictMode>
    {isExternalViewerPath(window.location.pathname) ? (
      <ExternalViewerApp queryClient={queryClient} />
    ) : (
      <QueryClientProvider client={queryClient}>
        <SessionScopeProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </SessionScopeProvider>
      </QueryClientProvider>
    )}
  </StrictMode>,
);
